'use client'

import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'
import { cn } from '@/lib/utils'

// ── Tooltip chain mode ───────────────────────────────────────────────────────
// Emil's rule: once the user has opened any tooltip, subsequent tooltips should
// appear instantly during the provider's timeout window and without an enter
// animation, so they still "chain" after the initial deliberate hover delay.
//
// Implementation: Base UI's Provider `timeout` handles the delay suppression
// natively (the Radix skipDelayDuration equivalent). It also sets
// `data-instant` on the popup for chained/instant opens, so CSS can zero the
// enter transition without any module-level mutable state (which caused stale
// reads on already-mounted tooltip instances).
// ─────────────────────────────────────────────────────────────────────────────

function TooltipProvider({
  delay = 320,
  timeout = 350,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return <TooltipPrimitive.Provider delay={delay} timeout={timeout} {...props} />
}

function Tooltip(props: TooltipPrimitive.Root.Props) {
  return <TooltipPrimitive.Root {...props} />
}

// data-slot wrapper adopted from upstream (base-nova) for slot coverage on
// the rendered trigger element.
function TooltipTrigger({ ...props }: TooltipPrimitive.Trigger.Props) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
  className,
  side,
  sideOffset = 0,
  align,
  alignOffset,
  collisionPadding,
  children,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<
    TooltipPrimitive.Positioner.Props,
    'side' | 'sideOffset' | 'align' | 'alignOffset' | 'collisionPadding'
  >) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        className="isolate z-[10100]"
        collisionPadding={collisionPadding}
        side={side}
        sideOffset={sideOffset}
      >
        <TooltipPrimitive.Popup
          className={cn(
            // Enter animation gated on non-instant opens: `data-instant`
            // (chaining) zeroes the transition duration so the tooltip appears
            // immediately.
            // z-tooltip stacks above modals/popovers so tooltips inside a popover
            // (e.g. disabled filter options) render on top instead of being clipped.
            // bg-popover/text-popover-foreground + border follows the active theme,
            // staying readable in both light and dark mode.
            'z-[10100] origin-(--transform-origin) rounded-md border border-border bg-popover px-2.5 py-1.5 trellis-product-ui text-body-2xs text-popover-foreground surface-elevated transition-[opacity,scale,translate] duration-[var(--duration-fast)] ease-[var(--ease-out)] data-ending-style:scale-95 data-ending-style:opacity-0 data-instant:duration-0 data-starting-style:scale-95 data-starting-style:opacity-0 data-[side=bottom]:data-starting-style:-translate-y-1 data-[side=left]:data-starting-style:translate-x-1 data-[side=right]:data-starting-style:-translate-x-1 data-[side=top]:data-starting-style:translate-y-1 rtl:data-[side=left]:data-starting-style:-translate-x-1 rtl:data-[side=right]:data-starting-style:translate-x-1',
            className
          )}
          data-slot="tooltip-content"
          {...props}
        >
          {children}
          {/* Base UI Arrow renders a plain <div>; the SVG replicates Radix's
              default 10x5 arrow. `fill-popover` is inherited by the polygon.
              Per-side offset/rotation replaces Radix's automatic rotation. */}
          {/* design-contract: allow-physical-direction -- the arrow follows the physical side reported by the popup positioning engine */}
          <TooltipPrimitive.Arrow className="fill-popover data-[side=bottom]:-top-[5px] data-[side=bottom]:rotate-180 data-[side=left]:-right-[7px] data-[side=left]:-rotate-90 data-[side=right]:-left-[7px] data-[side=right]:rotate-90 data-[side=top]:-bottom-[5px]">
            <svg
              aria-hidden="true"
              height="5"
              preserveAspectRatio="none"
              viewBox="0 0 30 10"
              width="10"
            >
              <polygon points="0,0 30,0 15,10" />
            </svg>
          </TooltipPrimitive.Arrow>
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
